/**
 * Per-yard pinecone tree cooldown + replenished pinecone snapshots.
 *
 * Survives Friends switches and layout-profile swaps within a session so tree
 * drops and consumed counts stay consistent without a new persistence layer.
 */

import type { PineconeInstanceRuntime } from '@/world-prototype/interaction/playerInteraction';
import { PINECONE_TREE_COOLDOWN_MS, PINECONE_TREE_YARD_ID } from '@/world-prototype/interaction/pineconeTree';

type YardTreeState = {
  cooldownUntilMs: number;
  replenished: Map<string, PineconeInstanceRuntime>;
};

const yards = new Map<string, YardTreeState>();

function yardState(yardId: string): YardTreeState {
  let state = yards.get(yardId);
  if (!state) {
    state = { cooldownUntilMs: 0, replenished: new Map() };
    yards.set(yardId, state);
  }
  return state;
}

export function pineconeTreeOnCooldown(yardId: string, nowMs = Date.now()): boolean {
  if (yardId !== PINECONE_TREE_YARD_ID) return true;
  return nowMs < yardState(yardId).cooldownUntilMs;
}

export function startPineconeTreeCooldown(
  yardId: string,
  nowMs = Date.now(),
): void {
  if (yardId !== PINECONE_TREE_YARD_ID) return;
  yardState(yardId).cooldownUntilMs = nowMs + PINECONE_TREE_COOLDOWN_MS;
}

export function replenishedPineconesForYard(
  yardId: string,
): ReadonlyMap<string, PineconeInstanceRuntime> {
  return yardState(yardId).replenished;
}

export function rememberReplenishedPinecone(
  yardId: string,
  runtime: PineconeInstanceRuntime,
): void {
  if (yardId !== PINECONE_TREE_YARD_ID) return;
  yardState(yardId).replenished.set(runtime.id, { ...runtime });
}

export function syncReplenishedPineconePose(
  yardId: string,
  runtime: PineconeInstanceRuntime,
): void {
  const saved = yardState(yardId).replenished.get(runtime.id);
  if (!saved) return;
  saved.x = runtime.x;
  saved.y = runtime.y;
  saved.rotation = runtime.rotation;
  saved.state = runtime.state;
  saved.width = runtime.width;
  saved.height = runtime.height;
  saved.containerId = runtime.containerId;
}

export function resetPineconeTreeStateForTests(): void {
  yards.clear();
}
